<script setup lang="ts">
import { useLiveStatsStore } from "@/features/live-stats/live-stats.store";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { Hourglass03 } from "utruckin-icons";

const liveStatsStore = useLiveStatsStore();

const now = ref(Date.now());
const totalBars = 30;

const workMaxNumber = computed(() => {
  return liveStatsStore.liveStatsData?.current_shift_users
    ? liveStatsStore.liveStatsData?.current_shift_users.length * 100
    : 0;
});

const workPercent = computed(() => {
  return Math.round(
    (shiftData.value?.counts.closed / workMaxNumber.value) * 100
  );
});

const workFilledBars = computed(() => {
  return Math.round((workPercent.value / 100) * totalBars);
});

const workBars = computed(() =>
  Array.from({ length: totalBars }, (_, i) => i < workFilledBars.value)
);

const kpiMaxNumber = computed(() => {
  return liveStatsStore.liveStatsData?.current_shift_users
    ? liveStatsStore.liveStatsData?.current_shift_users.length * 400
    : 0;
});

const kpiPercent = computed(() => {
  return Math.round((shiftData.value?.total_kpi / kpiMaxNumber.value) * 100);
});

const kpiFilledBars = computed(() => {
  return Math.round((kpiPercent.value / 100) * totalBars);
});

const kpiBars = computed(() =>
  Array.from({ length: totalBars }, (_, i) => i < kpiFilledBars.value)
);

let timer: ReturnType<typeof setInterval>;

const data = computed(() => {
  return liveStatsStore.liveStatsData;
});

const shiftData = computed(() => {
  const currentShiftName = liveStatsStore.liveStatsData?.current_shift?.name;
  let result = null;
  if (liveStatsStore.liveStatsData?.work_totals?.by_shift?.length) {
    liveStatsStore.liveStatsData?.work_totals?.by_shift.forEach((item) => {
      if (item.shift_name == currentShiftName) {
        result = item;
      }
    });
  }
  return result;
});

const remainingTime = computed(() => {
  const endTime = liveStatsStore.liveStatsData?.current_shift?.end_time;

  if (!endTime) {
    return "00 : 00 : 00";
  }

  const diff = Math.max(
    0,
    Math.floor((new Date(endTime).getTime() - now.value) / 1000)
  );

  const hours = Math.floor(diff / 3600);
  const minutes = Math.floor((diff % 3600) / 60);
  const seconds = diff % 60;

  return `${String(hours).padStart(2, "0")} : ${String(minutes).padStart(
    2,
    "0"
  )} : ${String(seconds).padStart(2, "0")}`;
});

onMounted(() => {
  timer = setInterval(() => {
    now.value = Date.now();
  }, 1000);
});

onBeforeUnmount(() => {
  clearInterval(timer);
});
</script>

<template>
  <div class="v-live-stats">
    <div class="table">
      <table>
        <colgroup>
          <col style="width: 1%" />
          <col style="width: 15%" />
          <col style="width: 7%" />
          <col style="width: 6%" />
          <col style="width: 6%" />
          <col style="width: 6%" />
          <col style="width: 6%" />
          <col style="width: 30%" />
        </colgroup>
        <thead>
          <tr>
            <th>No</th>
            <th>Username</th>
            <th>Team</th>
            <th>QA failed</th>
            <th>In progress</th>
            <th>Closed</th>
            <th>KPI</th>
            <th>Statistics</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td colspan="3">
              {{ data?.current_shift?.name }}
            </td>
            <td>{{ shiftData?.counts.qa_failed }}</td>
            <td>{{ shiftData?.counts.in_progress }}</td>
            <td>{{ shiftData?.counts.closed }}</td>
            <td>{{ shiftData?.total_kpi }}</td>
            <td></td>
          </tr>
          <tr
            v-for="(userData, userIndex) in data?.current_shift_users"
            :key="userData.user_id"
          >
            <td>{{ userIndex + 1 }}</td>
            <td>{{ userData.name }}</td>
            <td>{{ userData.team_name }}</td>
            <td>
              <span class="badge fail">{{ userData.counts.qa_failed }}</span>
            </td>
            <td>
              <span class="badge progress">{{
                userData.counts.in_progress
              }}</span>
            </td>
            <td>
              <span class="badge closed">{{ userData.counts.closed }}</span>
            </td>
            <td>
              <span class="badge kpi">{{ userData.total_kpi }}</span>
            </td>
            <td class="stats">
              <div class="progress">
                <div
                  class="bar"
                  :style="{ width: `${(userData.counts.closed * 100) / 100}%` }"
                ></div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="live">
      <div class="hour">
        <div class="hour__info">
          <div class="hour__info--text">Live</div>
          <div class="hour__info--icon"></div>
        </div>
        <div class="hour__time">
          <!--          <div class="icon">-->
          <!--            <Hourglass03 :size="18" />-->
          <!--          </div>-->
          <div class="time">{{ remainingTime }}</div>
        </div>
      </div>

      <div class="work">
        <div class="work__header">
          <div class="title">Work</div>
        </div>
        <div class="work__track">
          <div
            v-for="(filled, i) in workBars"
            :key="i"
            class="segment"
            :class="{ filled }"
          ></div>
        </div>

        <div class="work__info">
          <div class="percent">{{ workPercent }}%</div>

          <div class="number">
            {{ shiftData?.counts.closed }} / {{ workMaxNumber }}
          </div>
        </div>
      </div>

      <div class="kpi">
        <div class="kpi__header">
          <div class="title">KPI</div>
        </div>

        <div class="kpi__track">
          <div
            v-for="(filled, i) in kpiBars"
            :key="i"
            class="segment"
            :class="{ filled }"
          ></div>
        </div>

        <div class="kpi__info">
          <div class="percent">{{ kpiPercent }}%</div>

          <div class="number">
            {{ shiftData?.total_kpi }} / {{ kpiMaxNumber }}
          </div>
        </div>
      </div>

      <div class="violation">
        <div class="violation__title">Violations</div>

        <div class="violation__number">
          {{ data?.active_alerts?.eld_violations }}
        </div>
      </div>

      <div class="inspection">
        <div class="inspection__title">Inspections</div>

        <div class="inspection__number">
          {{ data?.inspections_today?.total }}
        </div>
      </div>

      <div class="upload">
        <div class="upload__title">No inspection files</div>

        <div class="upload__number">
          {{ data?.inspections_today?.no_output_files }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.v-live-stats {
  display: flex;
  gap: 8px;
  height: calc(100vh - 80px);
  padding: 8px 0;

  .table {
    background: var(--color-system-background);
    width: 80%;
    padding: 16px;
    border-radius: 20px;
    overflow: auto;

    table {
      width: 100%;
      border-collapse: separate;
      border: 1px solid var(--color-border);
      border-radius: 14px;
      border-spacing: 0;
      overflow: hidden;

      th,
      td {
        border-right: 1px solid var(--color-border);
        border-bottom: 1px solid var(--color-border);
        padding: 10px 16px;
      }

      th:last-child,
      td:last-child {
        border-right: none;
      }

      tr:last-child td {
        border-bottom: none;
      }

      thead {
        th {
          text-align: left;
          font-size: 14px;
          font-weight: 600;
          line-height: 20px;
          color: var(--color-label-secondary);
        }
      }

      tbody {
        td {
          font-size: 14px;
          line-height: 20px;
          font-weight: 600;
          letter-spacing: 0;
          color: var(--color-label);

          .badge {
            display: inline-block;
            width: 68px;
            padding: 4px 0;
            text-align: center;
            border-radius: 8px;

            &.fail {
              background: var(--color-red-light);
            }

            &.progress {
              background: var(--color-orange-light);
            }

            &.closed {
              background: var(--color-green-light);
            }

            &.kpi {
              background: var(--color-grey-light);
            }
          }

          &.stats {
            .progress {
              width: 100%;
              height: 100%;
              overflow: hidden;
              box-sizing: border-box;

              .bar {
                height: 24px;
                border-radius: 8px;
                background: var(--color-green-light);
              }
            }
          }
        }

        tr:first-child td {
          background-color: var(--color-secondary-system-background);
          font-size: 20px;
          line-height: 28px;
          font-weight: 600;
        }
      }
    }
  }

  .live {
    background: var(--color-system-background);
    width: 20%;
    border-radius: 20px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;

    .hour {
      border: 1px solid var(--color-border);
      border-radius: 16px;
      padding: 16px;
      display: flex;
      justify-content: space-between;
      align-items: center;

      &__info {
        padding: 9px 24px;
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 8px;
        border-radius: 14px;
        background: var(--color-red-light);

        &--text {
          font-size: 20px;
          line-height: 28px;
          font-weight: 600;
          color: var(--color-system-red);
        }

        &--icon {
          position: relative;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: var(--color-system-red);

          &::before {
            content: "";
            position: absolute;
            inset: 0;
            border-radius: 50%;
            background: var(--color-system-red);
            animation: livePulse 1.5s infinite;
          }
        }
      }

      &__time {
        font-size: 22px;
        line-height: 40px;
        font-weight: 600;
        color: var(--color-system-red);
        display: flex;
        align-items: center;
        gap: 4px;

        .icon {
          height: 40px;
          display: flex;
          justify-content: center;
          align-items: center;
        }
      }
    }

    .work,
    .kpi {
      border: 1px solid var(--color-border);
      border-radius: 16px;
      padding: 16px;

      &__header {
        margin-bottom: 24px;
        display: flex;

        .title {
          padding: 4px 12px;
          background: var(--color-green-light);
          border-radius: 8px;
          font-size: 14px;
          line-height: 20px;
          font-weight: 600;
          color: var(--color-label);
        }
      }

      &__track {
        display: flex;
        gap: 4px;
        width: 100%;
        margin-bottom: 4px;

        .segment {
          flex: 1;
          height: 60px;
          border-radius: 2px;
          background: var(--color-secondary-system-background);

          &.filled {
            background: var(--color-system-green);
          }
        }
      }

      &__info {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .percent {
          font-size: 20px;
          line-height: 28px;
          font-weight: 600;
          color: var(--color-system-green);
        }

        .number {
          font-size: 14px;
          line-height: 20px;
          font-weight: 600;
          color: var(--color-label);
        }
      }
    }

    .kpi {
      &__header {
        .title {
          background: var(--color-blue-light);
        }
      }

      &__track {
        .segment {
          &.filled {
            background: var(--color-system-blue);
          }
        }
      }

      &__info {
        .percent {
          color: var(--color-system-blue);
        }
      }
    }

    .violation,
    .inspection,
    .upload {
      border: 1px solid var(--color-border);
      border-radius: 16px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px;

      &__title {
        font-size: 16px;
        line-height: 24px;
        font-weight: 500;
        color: var(--color-label);
      }

      &__number {
        background: var(--color-secondary-system-background);
        padding: 4px 12px;
        border-radius: 12px;
        font-size: 16px;
        line-height: 24px;
        color: var(--color-label);
      }
    }
  }

  @keyframes livePulse {
    0% {
      transform: scale(1);
      opacity: 0.8;
    }

    70% {
      transform: scale(2);
      opacity: 0;
    }

    100% {
      transform: scale(2);
      opacity: 0;
    }
  }
}
</style>
